/**
 * Рост дохода руин на поле (см. scenes/RuinsIncomeAnimation): в первом раунде
 * окна увеличенного дохода камера отъезжает так, что все поле по вертикали в
 * кадре, поле темнеет, рядом с каждыми руинами появляется медальон с прежним
 * доходом, от центра поля идет волна света, и там, где она проходит, руины
 * вспыхивают, а медальон сжимается, раскаляется добела, рассыпается — и из света
 * рождается новый, крупнее и с новым доходом.
 * Длительности — в миллисекундах
 */
export const RUINS_INCOME_ANIMATION = {
  // Пауза перед отъездом камеры: модалка только что закрылась, поле и
  // интерфейс перерисовываются (см. CITY_CAPTURE_ANIMATION.settleMs)
  settleMs: 150,
  cameraMoveMs: 950,
  restoreCameraMs: 900,
  // Открытые панели гаснут и возвращаются за это время — как при облете
  panelsFadeMs: 250,

  // Кадр: все поле по вертикали, свободная область с запасом по краям. Руины
  // должны быть в кадре и по горизонтали: на телефоне стоймя кадр уже поля,
  // и камера отъезжает дальше, чтобы не осталось руин за краем. Маленькое поле
  // крупнее maxZoom не показывается
  fitShare: 0.95,
  maxZoom: 1.1,

  // Поле темнеет, а ячейки руин остаются светлыми
  dim: {
    color: 0x05070b,
    alpha: 0.62,
    fadeOutMs: 420,
    // Отступ затемнения за кадр: камера дрожит и не должна показать его край
    viewPaddingShare: 0.2,
  },

  // Медальоны «было»: появляются волной от центра поля, на всех — не дольше
  // revealSpreadMs. Затем пауза, чтобы прочитать прежний доход
  revealSpreadMs: 380,
  popMs: 320,
  beforeWaveHoldMs: 500,

  // Волна света: от центра поля за waveSpreadMs доходит до самых дальних руин
  waveSpreadMs: 950,
  // Сколько идет расширение за самые дальние руины, пока волна не погаснет
  waveTailMs: 420,
  // Обновление одних руин: столб света сходит раньше волны на beamLeadMs, столько
  // же медальон копит силу
  strike: {
    beamLeadMs: 260,
    beamTailMs: 420,
    beamWidthFactor: 1.5,
    beamHeightFactor: 11,
    flashMs: 480,
    flashSizeFactor: 3,
    flashAlpha: 0.7,
    totalMs: 1300,
    sparks: {
      count: 14,
      spawnMs: 360,
      lifeMs: 640,
      minSizeFactor: 0.35,
      maxSizeFactor: 0.7,
      startRadiusFactor: 0.8,
      endRadiusFactor: 0.3,
      turns: 0.8,
      swirlFlatten: 0.3,
      topJitterFactor: 0.4,
      topLiftFactor: 3.2,
    },
  },

  // Медальоны над руинами держат размер на экране, а не на поле: камера далеко.
  // Сторона — доля высоты и ширины кадра (на телефоне стоймя поле занимает лишь
  // полосу, и по одной высоте медальоны закрыли бы его), в CSS-пикселях не меньше
  // и не больше границ. Видимая часть медальона — visibleShare стороны: вокруг него
  // в картинке поля
  plate: {
    heightShare: 0.09,
    widthShare: 0.115,
    minCssPx: 44,
    maxCssPx: 84,
    visibleShare: 0.78,
  },
  // Медальон рядом со своими руинами: сверху, а если там другие руины или соседний
  // медальон — снизу, сбоку или наискось. Зазор — в долях стороны видимой части.
  // Немного медальоны могут заходить друг на друга: углы шестиугольников не касаются
  badge: {
    gapAboveHexShare: 0.05,
    overlapTolerance: 0.92,
  },

  // Перерождение медальона. Копит силу: сжимается, дрожит и раскаляется добела. На
  // ударе рассыпается вспышкой, а из нее вырастает новый с перелетом и остается
  // крупнее прежнего — доход усилился. Потом мерно дышит, пока итог на виду
  rebirth: {
    chargeScale: 0.78,
    chargeShakeShare: 0.04,
    chargeWhiteAlpha: 0.95,
    flareMs: 360,
    flareScale: 2.1,
    fromScale: 0.2,
    peakScale: 1.3,
    finalScale: 1.16,
    growMs: 240,
    settleMs: 210,
    breathScale: 1.05,
    breathMs: 560,
    // Эффекты вокруг медальона, размеры — в долях его видимой стороны
    cast: {
      totalMs: 1700,
      motes: {count: 12, startRadiusFactor: 2.4, sizeFactor: 0.34, turn: 2.2},
      flash: {durationMs: 460, sizeFactor: 3, alpha: 0.8},
      rays: {durationMs: 1200, sizeFactor: 5.2, innerShare: 0.7, turn: 1.1, alpha: 0.8},
      rings: {durationMs: 560, radiusFactor: 1.5, alpha: 0.7},
      shards: {count: 16, durationMs: 620, distanceFactor: 2.5, sizeFactor: 0.42, fall: 0.7},
      stream: {
        count: 10,
        spawnMs: 620,
        lifeMs: 620,
        minSizeFactor: 0.2,
        maxSizeFactor: 0.4,
        startRadiusFactor: 0.7,
        endRadiusFactor: 0.25,
        turns: 0.7,
        swirlFlatten: 0.3,
        topJitterFactor: 0.4,
        topLiftFactor: 2.2,
      },
    },
  },
  // Частицы на все руины: на поле с множеством руин у каждых их меньше, но не
  // меньше доли minParticleShare — тысяча спрайтов разом телефону ни к чему
  particleBudget: 400,
  particlesPerRuin: 52,
  minParticleShare: 0.3,

  // Итог на виду: новые медальоны дышат, руины светятся
  afterWaveHoldMs: 1150,
  outroMs: 320,

  // Дрожь камеры на первом ударе и на последнем
  shake: {durationMs: 260, intensity: 0.0035},

  // Отложенные звуки (см. configs/deferredSounds): нарастание — с отъездом
  // камеры, удар — с первой вспышкой, колокольчик — с каждыми следующими
  // руинами, не чаще strikeSoundGapMs и не больше strikeSoundLimit раз
  sounds: {
    riser: 'ruins_income_riser',
    impact: 'ruins_income_impact',
    strike: 'ruins_income_strike',
    strikeSoundGapMs: 140,
    strikeSoundLimit: 5,
  },

  // Сторож: как у облета — кадры могут встать (скрытая вкладка, потерянный WebGL)
  watchdogSlowdownFactor: 3,
  watchdogPaddingMs: 6000,
};

/**
 * Сколько идет анимация целиком: срок сторожа и проигрывателя записи. Времени
 * хода она не добавляет: снабжение идет параллельно у всех игроков
 * @param {Object} [config]
 * @returns {number}
 */
export const getRuinsIncomeAnimationDurationMs = (config = RUINS_INCOME_ANIMATION) => (
  config.settleMs + config.cameraMoveMs + config.revealSpreadMs + config.popMs + config.beforeWaveHoldMs
  + config.strike.beamLeadMs + config.waveSpreadMs + config.rebirth.growMs + config.rebirth.settleMs
  + config.afterWaveHoldMs + config.outroMs + config.restoreCameraMs
);
